import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import { ehTagDeTitulo, SELETOR_DE_TITULOS, tituloSemNumero } from '@miolos/core';

/**
 * O editor de texto — o TinyMCE, auto-hospedado.
 *
 * **Por que ele, depois de tanto trabalho no editor caseiro:** escrever um editor de
 * texto completo (tabela, lista, localizar, desfazer, colar do Word) é um produto
 * dentro do produto. O TinyMCE já é isso, e é maduro.
 *
 * **O que ele NÃO faz, e continua sendo nosso:** a folha da ABNT. O TinyMCE desenha o
 * texto dele dentro de um `iframe` com o CSS dele — margens de página, capitular,
 * quebra de capítulo, folha de anotação e numeração são do nosso desenho
 * (`DocumentoImpresso`), que segue intocado logo abaixo do editor. Esta divisão é o
 * que faz o editor ser "o rascunho bonito" e o papel ser "o que sai impresso".
 *
 * **A ponte é `documentoParaHtml`/`aplicarHtmlAoDocumento`** (no motor): o editor
 * recebe HTML e devolve HTML, e o que ele devolve vira seções com blocos — com os
 * ids das seções preservados.
 *
 * Três cuidados que estão no código:
 *
 * 1. **o React não escreve dentro do editor enquanto a pessoa escreve.** O conteúdo
 *    só é reinjetado (`setContent`) quando o HTML chegou de **fora** (abrir o
 *    documento, mexer no painel) — comparar com o último HTML que o editor produziu é
 *    o que impede o cursor de pular a cada tecla;
 * 2. **a barra de ferramentas do TinyMCE é curta de propósito** (`valid_elements`):
 *    só entra o que a norma imprime — negrito, itálico, lista, citação e tabela. Fonte,
 *    cor e alinhamento central ficariam no documento e **não** sairiam no papel, que é
 *    o tipo de mentira que a pessoa só descobre depois de imprimir;
 * 3. **a licença é declarada** (`license_key`): o pacote auto-hospedado é GPLv2+ ou
 *    licença comercial. A chave vem de variável de ambiente (`VITE_TINYMCE_LICENSE_KEY`)
 *    e o valor padrão é `gpl` — para usar a licença comercial, é só definir a variável.
 */

/** O que o editor oferece para o resto da tela. */
export interface EditorDeTextoHandle {
  /** Escreve no lugar onde o cursor está (é assim que a citação autor-data entra). */
  inserirNoCursor: (texto: string) => void;
  /*
   * **Título é nível de seção, não formatação de linha.** É a diferença entre este
   * editor e um processador de texto qualquer: no documento, um título **é** uma
   * seção — ele manda na numeração, na folha nova do capítulo e no sumário. Por isso
   * "Título 2" muda o nível da seção em que o cursor está, e não a linha do cursor.
   */
  nivelDaSecao: (tag: string) => void;
  /** O nível da seção em que o cursor está (`h1` a `h6`): a barra marca o botão. */
  secaoDoCursor: () => string;
  /** A linha do cursor vira o título de uma **seção nova** (título 1). */
  novaSecao: () => void;
  /**
   * Põe uma **figura** no lugar do cursor. O arquivo já foi enviado (quem envia é a tela,
   * pelo mesmo caminho da imagem de capa), e o que chega aqui é o endereço dele.
   */
  inserirImagem: (url: string, legenda?: string) => void;
  /**
   * Põe uma **nota** no lugar do cursor: no texto sai o número, e o texto da nota se escreve
   * no painel (é lá que ele tem lugar).
   */
  inserirNota: (numero: number) => void;
  /** A linha (ou o trecho) do cursor vira **citação longa** — o bloco recuado da norma. */
  transformarEmCitacao: () => void;
  /** Põe **aspas** no trecho selecionado: é a citação curta, que corre no meio do texto. */
  darAspas: () => void;
  /** Leva o cursor ao título da seção com este texto (é o que o sumário faz). */
  irParaSecao: (titulo: string) => void;
  /**
   * Anda o recuo do bloco onde o cursor está — um degrau para dentro (`1`) ou para
   * fora (`-1`). Se houver trecho selecionado, vale para os blocos dele.
   */
  recuoDoBloco: (passo: 1 | -1) => void;
  /** Grava agora o que está escrito (usado antes de salvar). */
  sincronizar: () => void;
}

interface Props {
  /** O documento em HTML (vem do motor, via `documentoParaHtml`). */
  html: string;
  /**
   * Muda quando o texto veio **de fora** do editor (abrir o documento, mexer no
   * painel, resposta do servidor). É o gatilho do `setContent` — e a razão de ele
   * não disparar a cada tecla.
   */
  versao: number;
  /** O que o editor escreveu, em HTML. */
  aoMudar: (html: string) => void;
  /**
   * O bloco em que o cursor está agora: o tipo (`p`, `h1`, ...) e o recuo dele em
   * degraus — `null` quando o bloco não aceita recuo (um título, por exemplo). É o
   * que a barra usa para marcar o botão do nível e desabilitar o recuo na ponta.
   */
  aoTrocarBloco?: (estado: { nivel: string; recuo: number | null }) => void;
  /** Ctrl+S no editor: salvar o documento. */
  aoSalvar?: () => void;
  /** O CSS do corpo do texto: é ele que faz o editor parecer o papel. */
  estiloDoCorpo: string;
  /** A altura da área de escrita, em pixels. */
  altura?: number;
  /**
   * Quantos degraus de recuo o papel aceita (o teto vem do motor,
   * `degrausDeRecuoDoPapel`). Passando disto, o botão de aumentar recuo para de
   * andar — em vez de empurrar o texto para fora da mancha.
   */
  maximoDeRecuo?: number;
  ref?: Ref<EditorDeTextoHandle>;
}

/**
 * O bloco de texto que o cursor ocupa: o `p`, `h1`… da linha onde se escreve.
 *
 * É nele que o `FormatBlock` do editor age (a seleção pode estar dentro de um
 * `strong`, por exemplo, e o comando vale para o bloco inteiro).
 */
function blocoDoCursor(corpo: HTMLElement, no: Element | null): Element | null {
  let atual: Element | null = no;
  while (atual && atual !== corpo) {
    if (/^(P|H1|H2|H3|LI|BLOCKQUOTE)$/.test(atual.tagName)) return atual;
    atual = atual.parentElement;
  }
  return null;
}

/**
 * O bloco do cursor **já está** no formato pedido?
 *
 * É a guarda do `FormatBlock`, que **alterna**: pedir o formato que o bloco já tem o
 * devolve a parágrafo. Era o defeito: **Título 1** numa seção que já era nível 1
 * transformava o título em texto do corpo — no editor a pessoa via o título virar
 * parágrafo, o papel não mudava (o documento só é lido quando o texto muda de fato) e,
 * ao salvar, o título ia embora junto com os parágrafos. O mesmo valia para
 * **Seção nova** clicada sobre um título, e para a lista de formatos do próprio editor.
 */
function jaEstaNoFormato(corpo: HTMLElement, no: Element | null, tag: string): boolean {
  return blocoDoCursor(corpo, no)?.tagName.toLowerCase() === tag.toLowerCase();
}

/**
 * O título da seção em que o cursor está: o `h1`/`h2`/`h3` mais próximo **antes**
dele (ou ele mesmo, quando o cursor está no próprio título).
 *
 * É o que permite "Título 2" querer dizer "esta seção é subseção", que é o que a
 * pessoa quer dizer quando clica nisso no meio de um parágrafo.
 */
function tituloDaSecao(corpo: HTMLElement, no: Element | null): Element | null {
  if (no && /^H[123]$/.test(no.tagName)) return no;

  const titulos = Array.from(corpo.querySelectorAll(SELETOR_DE_TITULOS));
  const antes = no
    ? titulos.filter((titulo) => Boolean(titulo.compareDocumentPosition(no) & Node.DOCUMENT_POSITION_FOLLOWING))
    : titulos;
  return antes[antes.length - 1] ?? titulos[0] ?? null;
}

/**
 * O texto sem acento, sem caixa alta e **sem as marcas do Markdown**, para comparar
 * títulos de seção.
 *
 * As marcas saem porque o sumário do documento guarda `**negrito**` (é o texto do
 * título, do jeito que ele vive no documento) e o editor desenha `<strong>negrito</strong>`:
 * sem tirar os asteriscos, clicar no sumário de um capítulo com uma palavra em
 * negrito não acharia o título e o cursor não iria a lugar nenhum.
 */
function semAcento(texto: string): string {
  return texto
    .replace(/\*/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

/**
 * O bloco que **recebe o recuo**: o parágrafo, a citação longa ou a lista.
 *
 * Sobe do nó do cursor até achar um deles: dentro de uma lista (`li`), quem anda é a
 * lista inteira — recuar um item solto deixaria a marcação desalinhada da própria
 * coluna. Título não entra na conta: no documento acadêmico o título **é** a seção, e
 * a seção abre na margem (é a norma, não uma escolha) — por isso o retorno é `null` e
 * a barra desabilita os dois botões quando o cursor está nele.
 */
function blocoDeRecuo(corpo: HTMLElement, no: Element | null): HTMLElement | null {
  let atual: Element | null = no;
  while (atual && atual !== corpo) {
    const tag = atual.tagName.toLowerCase();
    if (tag === 'li') {
      const lista = atual.parentElement;
      return lista && lista !== corpo ? lista : null;
    }
    if (tag === 'p' || tag === 'blockquote' || tag === 'ul' || tag === 'ol') return atual as HTMLElement;
    atual = atual.parentElement;
  }
  return null;
}

/** O recuo que um bloco tem hoje, em degraus (o atributo é texto no HTML). */
function leituraDeRecuo(no: HTMLElement | null): number {
  const bruto = Number.parseInt(no?.getAttribute('data-recuo') ?? '', 10);
  return Number.isFinite(bruto) && bruto > 0 ? bruto : 0;
}

/**
 * Os blocos que a seleção cobre.
 *
 * O editor de texto sabe devolver isso; quando ele não sabe (cursor sem seleção, ou
 * uma versão que não exponha a lista), vale o nó em que o cursor está — é o caso
 * comum de quem clica sem selecionar nada.
 */
function blocosDaSelecao(atual: EditorTiny, corpo: HTMLElement): Element[] {
  const inicio = atual.selection.getNode();
  const selecionados = atual.selection.getSelectedBlocks?.() ?? [];
  const dentro = selecionados.filter((no) => corpo.contains(no));
  if (dentro.length > 0) return dentro;
  return inicio ? [inicio] : [];
}

/**
 * O que a barra precisa saber do lugar onde o cursor está: o nível da seção (para
 * marcar Título 1 a 6) e o recuo do bloco, em degraus — `null` quando ali não há
 * bloco que aceite recuo (um título), e é isso que desabilita os dois botões.
 */
function estadoDoCursor(atual: EditorTiny, corpo: HTMLElement): { nivel: string; recuo: number | null } {
  const no = atual.selection.getNode();
  const titulo = tituloDaSecao(corpo, no);
  const tag = titulo?.tagName.toLowerCase() ?? '';
  const bloco = blocoDeRecuo(corpo, no);
  return {
    nivel: ehTagDeTitulo(tag) ? tag : 'h1',
    recuo: bloco ? leituraDeRecuo(bloco) : null,
  };
}

/**
 * Tira o número digitado do começo do título — "4. Metodologia" vira "Metodologia".
 *
 * Quem escreve no Word costuma numerar à mão, e no gerador quem numera é o motor, pela
 * ordem das seções: o número digitado fica mentindo assim que uma seção muda de lugar —
 * e o papel sairia com dois números ("1. 4. Metodologia").
 *
 * A limpeza é feita **no texto do nó**, e não refazendo o HTML do título: o negrito e o
 * itálico do resto do título não podem se perder porque alguém digitou um número na
 * frente. Título que era só o número ("4.") fica como está — tirar tudo deixaria a seção
 * sem nome, e aí o gerador avisa que falta título.
 *
 * Devolve `true` quando mexeu no texto: quem chama avisa a tela, porque o documento
 * mudou mesmo sem ninguém ter digitado nada agora.
 */
function limparNumeroDoTitulo(alvo: HTMLElement | null): boolean {
  if (!alvo) return false;
  const texto = alvo.textContent ?? '';
  const limpo = tituloSemNumero(texto);
  if (limpo === texto || !limpo) return false;

  /* Quantos caracteres saíram da frente (o resto do título continua igual). */
  const consumidos = texto.indexOf(limpo);
  if (consumidos <= 0) return false;

  let faltam = consumidos;
  const caminhante = alvo.ownerDocument.createTreeWalker(alvo, NodeFilter.SHOW_TEXT);
  const vazios: Text[] = [];
  for (let no = caminhante.nextNode() as Text | null; no && faltam > 0; no = caminhante.nextNode() as Text | null) {
    if (faltam >= no.data.length) {
      faltam -= no.data.length;
      vazios.push(no);
      continue;
    }
    no.data = no.data.slice(faltam);
    faltam = 0;
  }
  vazios.forEach((no) => no.remove());
  return true;
}

/**
 * Põe o conteúdo no editor — e um tropeço dele **não** pode apagar a tela.
 *
 * O `setContent` do TinyMCE estoura quando o editor foi mexido fora de hora, e a exceção
 * que sobe de um efeito do React desmonta a página inteira (foi assim que a segunda
 * abertura do documento ficava em branco, sem cabeçalho nem nada). Aqui o tropeço vira o
 * aviso que a pessoa lê — o mesmo caminho do editor que não carrega.
 */
function darConteudo(editor: EditorTiny, html: string): string {
  try {
    editor.setContent(html);
    return '';
  } catch (causa) {
    return causa instanceof Error ? causa.message : 'O editor não aceitou o conteúdo.';
  }
}

/** O endereço do motor do editor no site (copiado do `node_modules` no build). */
const BASE = '/tinymce';
const CHAVE = (import.meta.env.VITE_TINYMCE_LICENSE_KEY as string | undefined) ?? 'gpl';

/** O TinyMCE carrega uma vez só, por mais editores que a página abra. */
let carregando: Promise<void> | null = null;

function carregarTiny(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const janela = window as unknown as { tinymce?: unknown };
  if (janela.tinymce) return Promise.resolve();
  if (carregando) return carregando;

  carregando = new Promise<void>((resolve, rejeitar) => {
    const script = document.createElement('script');
    script.src = `${BASE}/tinymce.min.js`;
    script.referrerPolicy = 'origin';
    script.onload = () => resolve();
    script.onerror = () => rejeitar(new Error('Não foi possível carregar o editor de texto.'));
    document.head.appendChild(script);
  });
  return carregando;
}

/**
 * O evento do editor, na parte que a guarda do formato usa.
 *
 * O `BeforeExecCommand` é **cancelável**: `preventDefault()` impede o comando de
 * rodar — é assim que a lista de formatos do editor para de alternar o bloco.
 */
interface EventoDoEditor {
  command?: string;
  value?: unknown;
  /** Do `keydown` de dentro do editor (`Ctrl+S` salva com o cursor no texto). */
  key?: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  preventDefault: () => void;
}

/** O tipo do editor, na parte que a gente usa (a tipagem oficial é enorme). */
interface EditorTiny {
  getContent: () => string;
  setContent: (html: string) => void;
  insertContent: (html: string) => void;
  execCommand: (comando: string, ui: boolean, valor?: string) => boolean;
  queryCommandValue: (comando: string) => string;
  /**
   * Um evento do editor. O retorno recebe o evento daquele nome: os que interessam
   * aqui (`BeforeExecCommand`) trazem o comando, o valor e o `preventDefault` — é por
   * ele que a guarda do formato cancela o comando antes de ele alternar o bloco.
   */
  on: (evento: string, retorno: (evento?: EventoDoEditor) => void) => void;
  once: (evento: string, retorno: () => void) => void;
  focus: () => void;
  hasFocus: () => boolean;
  selection: {
    select: (no: Element) => void;
    getNode: () => Element | null;
    getRng: () => Range | null;
    getSelectedBlocks?: () => Element[];
    setCursorLocation: (no: Node, inicio?: boolean) => void;
  };
  /**
   * O DOM do editor. É por ele que a **figura** entra: `insertContent` de um bloco
   * dentro de outro bloco faz o editor reorganizar o bloco (e o que estava dentro dele se
   * perde) — com o `dom`, a figura é criada e inserida **depois** do bloco do cursor.
   */
  dom: {
    getParent: (no: Node | null, seletor: string) => HTMLElement | null;
    create: (tag: string, atributos?: Record<string, string>, html?: string) => HTMLElement;
    add: (pai: HTMLElement, tag: string, atributos?: Record<string, string>) => HTMLElement;
    insertAfter: (novo: Node, referencia: Node) => Node;
  };
  remove: () => void;
  mode: { set: (modo: string) => void };
  getBody: () => HTMLElement;
  isDirty: () => boolean;
  setDirty: (sujo: boolean) => void;
  /**
   * O histórico do editor. `transact` envolve uma mudança feita direto no HTML (o
   * recuo do bloco, que é atributo e não comando do editor) e cria o nível de
   * desfazer — sem ele, `Ctrl+Z` não voltaria o recuo.
   */
  undoManager: { transact: (retorno: () => void) => unknown };
}

/**
 * O que a norma imprimi — e só isso.
 *
 * Tudo o que não está nesta lista é **jogado fora na colagem** (com o texto preservado):
 * uma cor de letra ou um alinhamento central viriam do Word, ficariam no documento e
 * não sairiam no papel.
 */
const ELEMENTOS_VALIDOS = [
  /* O recuo do bloco viaja como atributo (`data-recuo`), e não como `style`. */
  'p[data-recuo]',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote[data-recuo]',
  'ul[data-recuo]',
  'ol[data-recuo]',
  'li',
  'table[data-legenda|data-fonte]',
  /*
   * A figura no meio do texto e a nota no texto. A legenda, a fonte e a largura da figura
   * viajam em **atributos** (como as da tabela) e o texto da nota também: no editor a pessoa
   * mexe na imagem e escreve o texto da nota no painel — sem os atributos, o que ela escreveu
   * se perderia a cada edição.
   */
  'figure[data-legenda|data-fonte|data-largura]',
  'img[src|alt|data-legenda|data-fonte|data-largura]',
  'figcaption',
  'sup[data-nota|data-nota-texto]',
  'thead',
  'tbody',
  'tr',
  'th[colspan|rowspan|scope]',
  'td[colspan|rowspan]',
  'strong/b',
  'em/i',
  'br',
  'a[href|target]',
].join(',');

export function EditorDeTexto({
  html,
  versao,
  aoMudar,
  aoTrocarBloco,
  aoSalvar,
  estiloDoCorpo,
  altura = 520,
  maximoDeRecuo = 8,
  ref,
}: Props) {
  const caixa = useRef<HTMLDivElement>(null);
  const editor = useRef<EditorTiny | null>(null);
  /**
   * O editor **está pronto** para receber conteúdo?
   *
   * O `setup` do TinyMCE entrega a instância antes de ela estar montada, e mexer nela
   * nesse meio-tempo estoura dentro do próprio TinyMCE (`setContent` lê o analisador
   * quando ele ainda não existe). O estouro não é capturado por ninguém — e uma exceção
   * que sobe de um efeito do React **desmonta a página inteira**: a tela ficava em
   * branco, sem nem o cabeçalho.
   *
   * Acontecia na **segunda** abertura do documento: com o TinyMCE no cache do navegador,
   * o efeito que injeta o conteúdo (o da `versao`) chegava antes do `init` do editor.
   */
  const pronto = useRef(false);
  /** O conteúdo que chegou antes de o editor ficar pronto: entra no `init`. */
  const pendente = useRef<string | null>(null);
  /** O editor tropeçou: a tela avisa em vez de sumir. */
  const [tropeco, setTropeco] = useState('');
  /** O último HTML que **o editor** produziu: é o que evita reinjetar o do documento. */
  const ultimoDoEditor = useRef<string>('');
  const religando = useRef(false);
  const aoMudarAtual = useRef(aoMudar);
  const aoSalvarAtual = useRef(aoSalvar);
  const aoTrocarBlocoAtual = useRef(aoTrocarBloco);
  const estiloAtual = useRef(estiloDoCorpo);
  /** O teto do recuo, vindo do motor (muda com o papel: A4 aceita mais que A5). */
  const maximoDeRecuoAtual = useRef(maximoDeRecuo);

  aoMudarAtual.current = aoMudar;
  aoSalvarAtual.current = aoSalvar;
  aoTrocarBlocoAtual.current = aoTrocarBloco;
  estiloAtual.current = estiloDoCorpo;
  maximoDeRecuoAtual.current = maximoDeRecuo;

  /**
   * Avisa a tela de que o texto mudou sem o `input` do editor.
   *
   * Dois gestos mexem no HTML por fora do editor — o recuo do bloco e o número que sai do
   * título —, e nos dois o documento **é** outro: sem este aviso, o aviso de "mudanças não
   * salvas" não acenderia e a prévia do papel ficaria para trás.
   */
  const avisarMudanca = () => {
    const atual = editor.current;
    if (!atual) return;
    const conteudo = atual.getContent();
    ultimoDoEditor.current = conteudo;
    aoMudarAtual.current(conteudo);
  };

  /**
   * O título do bloco do cursor, **sem o número digitado**.
   *
   * É o que o gesto de marcar um título faz sozinho: quem escreve "4. Metodologia" e
   * clica em **Título 1** espera ver "Metodologia" — o número é do gerador, que numera
   * pela ordem das seções. Devolve `true` quando mexeu (e quem chama avisa a tela).
   */
  const limparNumeroDoTituloDoCursor = (atual: EditorTiny, corpo: HTMLElement): boolean => {
    const alvo = tituloDaSecao(corpo, atual.selection.getNode());
    return alvo ? limparNumeroDoTitulo(alvo as HTMLElement) : false;
  };

  useImperativeHandle(
    ref,
    () => ({
      inserirNoCursor: (texto: string) => {
        const atual = editor.current;
        /* Sem o editor pronto não há cursor onde inserir (e mexer nele estoura). */
        if (!atual || !pronto.current) return;
        atual.focus();

        /*
         * O espaço antes e depois é decidido pelo que está em volta: sem isto a
         * citação sairia colada na última palavra ("mudou(SOUZA, 2020)") ou com um
         * espaço a mais, no meio de outra palavra ("a (SOUZA, 2020) lfabetização"
         * quando o certo é "alfabetização").
         */
        const faixa = atual.selection.getRng();
        const irmao = faixa?.startContainer;
        const conteudo = irmao?.textContent ?? '';
        const antes = faixa ? conteudo.slice(0, faixa.startOffset) : '';
        const depois = faixa ? conteudo.slice(faixa.endOffset) : '';
        const prefixo = /[\p{L}\p{N}]$/u.test(antes) ? ' ' : '';
        const sufixo = /^[\p{L}\p{N}]/u.test(depois) ? ' ' : '';

        /* Escapado: o `insertContent` interpreta o que recebe como HTML. */
        const escapado = `${prefixo}${texto}${sufixo}`
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        atual.insertContent(escapado);
      },
      nivelDaSecao: (tag) => {
        const atual = editor.current;
        const corpo = atual?.getBody();
        if (!atual || !corpo) return;

        const alvo = tituloDaSecao(corpo, atual.selection.getNode());
        if (!alvo) return;

        /*
         * A seção **já está** neste nível? Não faz nada. O `FormatBlock` alterna, e o
         * botão do nível em que a seção está devolveria o título a parágrafo — a
         * pessoa via o título virar texto do corpo e o papel não mudar.
         */
        if (alvo.tagName.toLowerCase() === tag.toLowerCase()) return;

        atual.focus();
        atual.selection.select(alvo);
        atual.execCommand('FormatBlock', false, tag);

        /*
         * O número digitado no título sai junto com o gesto — e o nó é procurado de novo
         * porque o `FormatBlock` pode trocar o elemento de lugar.
         */
        if (limparNumeroDoTituloDoCursor(atual, corpo)) avisarMudanca();
      },
      secaoDoCursor: () => {
        const atual = editor.current;
        const corpo = atual?.getBody();
        if (!atual || !corpo) return 'h1';

        const alvo = tituloDaSecao(corpo, atual.selection.getNode());
        const tag = alvo?.tagName.toLowerCase() ?? '';
        return ehTagDeTitulo(tag) ? tag : 'h1';
      },
      novaSecao: () => {
        const atual = editor.current;
        const corpo = atual?.getBody();
        if (!atual || !corpo) return;

        /*
         * A **linha** do cursor vira o título de uma seção nova (título 1). É o gesto
         * de quem escreveu um texto corrido e agora quer dividi-lo: escreve a linha
         * do título, clica aqui, e o resto vem sozinho (numeração, folha nova,
         * sumário).
         *
         * Se a linha **já é** um título de nível 1, não há seção nova a abrir — e o
         * `FormatBlock` alternaria, devolvendo o título a parágrafo.
         */
        if (jaEstaNoFormato(corpo, atual.selection.getNode(), 'h1')) return;

        atual.focus();
        atual.execCommand('FormatBlock', false, 'h1');

        /* O número digitado ("4. Metodologia") sai: que numera é o motor. */
        if (limparNumeroDoTituloDoCursor(atual, corpo)) avisarMudanca();
      },
      inserirImagem: (url: string, legenda?: string) => {
        const atual = editor.current;
        if (!atual || !url.trim()) return;

        /*
         * O endereço e a legenda são escapados: o `insertContent` interpreta o que recebe
         * como HTML, e um `&` no endereço partiria a marcação.
         */
        const endereco = url.trim();
        const rotulo = legenda ?? '';

        /*
         * A figura entra como **bloco irmão**, logo depois do bloco do cursor — e não dentro
         * dele pelo `insertContent`.
         *
         * O `insertContent` de um elemento de bloco dentro de outro bloco não é inofensivo: o
         * editor reorganiza o bloco para aceitar o filho (uma citação vira parágrafo, por
         * exemplo) e o que estava dentro dele — a nota, outra figura — se perde. Com o `dom`,
         * a figura é criada e inserida depois do bloco, sem tocar no que já estava lá.
         */
        const bloco = atual.dom.getParent(
          atual.selection.getNode(),
          'p,blockquote,ul,ol,table,figure,h1,h2,h3,h4,h5,h6',
        );
        const figura = atual.dom.create('figure', { 'data-legenda': rotulo });
        atual.dom.add(figura, 'img', { src: endereco, alt: rotulo });

        atual.focus();
        if (bloco?.parentNode) atual.dom.insertAfter(figura, bloco);
        else atual.getBody().appendChild(figura);

        /* O cursor vai para depois da figura: quem inseriu outra imagem continua de onde parou. */
        atual.selection.setCursorLocation(figura, false);
        avisarMudanca();
      },
      inserirNota: (numero: number) => {
        const atual = editor.current;
        if (!atual) return;
        atual.focus();
        /*
         * Só o **número** entra no texto; o texto da nota viaja no atributo, e quem o escreve
         * é o painel das notas. O número vai **dentro** do `sup` de propósito: marcação vazia
         * o editor descarta na hora de inserir (e a nota sumiria sem aviso).
         */
        atual.insertContent(`<sup data-nota="${numero}" data-nota-texto="">${numero}</sup>`);
        avisarMudanca();
      },
      transformarEmCitacao: () => {
        const atual = editor.current;
        const corpo = atual?.getBody();
        if (!atual || !corpo) return;

        /*
         * A citação longa é um **bloco**: ela pega o parágrafo inteiro em que o cursor está
         * (a norma recua o trecho todo, não uma palavra). Quem já está numa citação fica
         * como está — o `FormatBlock` alternaria e devolveria o trecho a parágrafo.
         */
        if (jaEstaNoFormato(corpo, atual.selection.getNode(), 'blockquote')) return;
        atual.focus();
        atual.execCommand('FormatBlock', false, 'blockquote');
        avisarMudanca();
      },
      darAspas: () => {
        const atual = editor.current;
        if (!atual) return;
        /* O que está selecionado, em texto puro (`getContent` não está no tipo do editor). */
        const selecionado = (atual.selection.getRng()?.toString() ?? '').trim();
        atual.focus();
        /* Com trecho selecionado, as aspas o abraçam; sem seleção, elas esperam o texto. */
        atual.insertContent(`“${selecionado}”`);
        avisarMudanca();
      },
      irParaSecao: (titulo: string) => {
        const atual = editor.current;
        const corpo = atual?.getBody();
        if (!atual || !corpo) return;

        /*
         * O título é procurado pelo texto: o editor não guarda o id da seção (quem
         * guarda é o documento, e é ele que numera e imprime). A comparação ignora
         * acentos e caixa porque o título que sai no sumário é o do documento, e o
         * que está escrito no editor passou por marcação de negrito e itálico.
         */
        const procurado = semAcento(titulo);
        const alvo = Array.from(corpo.querySelectorAll(SELETOR_DE_TITULOS)).find(
          (no) => semAcento(no.textContent ?? '') === procurado,
        );
        if (!alvo) return;

        atual.focus();
        atual.selection.select(alvo);
        alvo.scrollIntoView({ block: 'center', behavior: 'smooth' });
      },
      sincronizar: () => {
        const atual = editor.current;
        if (!atual) return;
        const conteudo = atual.getContent();
        ultimoDoEditor.current = conteudo;
        aoMudarAtual.current(conteudo);
      },      recuoDoBloco: (passo) => {
        const atual = editor.current;
        const corpo = atual?.getBody();
        if (!atual || !corpo) return;

        /*
         * Quem anda é o **bloco** (parágrafo, citação ou lista), e não a linha do
         * cursor: recuar meia frase não existe no papel. Com trecho selecionado, andam
         * todos os blocos que ele cobre.
         */
        const alvos = new Set<HTMLElement>();
        for (const no of blocosDaSelecao(atual, corpo)) {
          const alvo = blocoDeRecuo(corpo, no);
          if (alvo) alvos.add(alvo);
        }
        if (alvos.size === 0) return;

        /*
         * A mudança é de **atributo** (é assim que o recuo atravessa a ponte do
         * editor para o documento, sem virar `style` que ninguém sabe ler). O
         * `transact` é o que a coloca no histórico: sem ele, `Ctrl+Z` pularia o
         * recuo como se ele nunca tivesse existido.
         */
        atual.undoManager.transact(() => {
          for (const alvo of alvos) {
            const degrau = Math.min(
              Math.max(leituraDeRecuo(alvo) + passo, 0),
              maximoDeRecuoAtual.current,
            );
            if (degrau > 0) alvo.setAttribute('data-recuo', String(degrau));
            else alvo.removeAttribute('data-recuo');
          }
        });

        /*
         * O texto não mudou (o recuo é formato), mas o documento **é** outro: avisar a
         * tela é o que marca "mudanças não salvas" e faz a prévia do papel andar junto.
         */
        avisarMudanca();
        aoTrocarBlocoAtual.current?.(
          estadoDoCursor(atual, corpo),
        );
      },    }),
    [],
  );

  useEffect(() => {
    let vivo = true;

    void carregarTiny().then(() => {
      if (!vivo || !caixa.current) return;
      const tiny = (window as unknown as { tinymce: { init: (config: Record<string, unknown>) => Promise<EditorTiny> } }).tinymce;

      void tiny
        .init({
          target: caixa.current,
          base_url: BASE,
          suffix: '.min',
          license_key: CHAVE,
          menubar: false,
          branding: false,
          promotion: false,
          statusbar: true,
          height: altura,
          resize: true,
          /*
           * A barra: o que a norma imprime. Tabela e lista vêm do próprio editor — é
           * justamente o que motivou trocar o editor caseiro por ele.
           */
          plugins: 'lists table code searchreplace wordcount',
          toolbar:
            'blocks | bold italic | bullist numlist | blockquote | table | removeformat | undo redo | searchreplace code',
          /*
           * O endereço da imagem fica **como o servidor o deu** (`/uploads/...`).
           *
           * Sem estas três linhas o editor reescreve o endereço para o caminho **relativo à
           * página** (`../uploads/...`), e o gerador gravava isso: além de a figura só
           * aparecer em algumas telas, a API recusa `../` no corpo (é o padrão de subida de
           * diretório da varredura de segurança) e a gravação do documento inteiro falhava.
           */
          relative_urls: false,
          convert_urls: false,
          remove_script_host: false,
          block_formats: 'Parágrafo=p; Título 1=h1; Título 2=h2; Título 3=h3; Título 4=h4; Título 5=h5; Título 6=h6',
          contextmenu: 'link table',
          /*
           * O acento sai como a letra, e não como entidade (`&aacute;`): o nosso
           * documento guarda texto, e a ponte já entende entidades — mas o que é
           * gravado fica legível no banco e igual ao que a pessoa escreveu.
           */
          entity_encoding: 'raw',
          valid_elements: ELEMENTOS_VALIDOS,
          extended_valid_elements: 'table[data-legenda|data-fonte]',
          /* Colar do Word entra como texto com as marcas que a norma aceita. */
          paste_webkit_styles: 'none',
          paste_merge_formats: true,
          paste_as_text: false,
          /*
           * Sem folha de estilo do pacote: quem manda no corpo do texto é a nossa,
           * injetada com id para poder ser trocada (o formato do parágrafo muda) sem
           * mexer no texto nem no cursor.
           */
          content_css: false,
          skin: 'oxide',
          setup: (instancia: EditorTiny) => {
            editor.current = instancia;
            instancia.on('init', () => {
              const documento = instancia.getBody()?.ownerDocument;
              if (documento) {
                const folha = documento.createElement('style');
                folha.id = 'estilo-do-documento';
                folha.textContent = estiloAtual.current;
                documento.head.appendChild(folha);
              }

              /*
               * Daqui para a frente a instância aceita conteúdo. O que chegou antes (o
               * efeito da `versao` pode rodar primeiro, quando o TinyMCE vem do cache do
               * navegador) entra agora — é o caso da segunda abertura do documento.
               */
              pronto.current = true;
              const conteudo = pendente.current ?? html;
              pendente.current = null;

              const falha = darConteudo(instancia, conteudo);
              if (falha) setTropeco(falha);
              else ultimoDoEditor.current = conteudo;
            });
            /*
             * `Ctrl+S` **dentro do texto** também salva.
             *
             * O atalho de fora (`document`) não basta: com o cursor no editor, o teclado
             * está dentro do `iframe`, e o evento não sobe para o documento da página — o
             * gesto mais natural de quem está escrevendo era o único que não salvava.
             */
            instancia.on('keydown', (evento) => {
              if ((evento?.ctrlKey || evento?.metaKey) && evento?.key?.toLowerCase() === 's') {
                evento.preventDefault();
                aoSalvarAtual.current?.();
              }
            });

            instancia.on('input change undo redo', () => {
              if (religando.current) return;
              const conteudo = instancia.getContent();
              ultimoDoEditor.current = conteudo;
              aoMudarAtual.current(conteudo);
            });
            /*
             * A lista de formatos do **próprio editor** (Parágrafo / Título 1 a 6)
             * traz o mesmo "alterna" do comando — e por um caminho diferente do
             * `FormatBlock`: ela sai por `mceToggleFormat`, que chama o `formatter` do
             * editor direto. Os dois passam por aqui, e a guarda vale para os dois: o
             * formato que a linha já tem não a devolve a parágrafo.
             */
            instancia.on('BeforeExecCommand', (evento) => {
              const comando = evento?.command ?? '';
              if (comando !== 'FormatBlock' && comando !== 'mceToggleFormat') return;
              const formato = String(evento?.value ?? '').toLowerCase();
              if (!/^(p|h[1-6])$/.test(formato)) return;
              const corpo = instancia.getBody();
              if (!corpo || !jaEstaNoFormato(corpo, instancia.selection.getNode(), formato)) return;
              evento?.preventDefault();
            });
            /*
             * O cursor andou: a barra precisa saber o **nível da seção** (para marcar
             * Título 1 a 6) e o **recuo do bloco** (para saber se o botão de
             * aumentar ainda anda e se o de diminuir já chegou à margem).
             */
            instancia.on('NodeChange', () => {
              const corpo = instancia.getBody();
              if (!corpo) return;
              aoTrocarBlocoAtual.current?.(estadoDoCursor(instancia, corpo));
            });
          },
        })
        .catch(() => {
          /* Sem o editor, a tela mostra o aviso do erro (quem chama trata). */
        });
    });

    return () => {
      vivo = false;
      pronto.current = false;
      pendente.current = null;
      editor.current?.remove();
      editor.current = null;
    };
    // A altura e o estilo inicial não mudam durante a vida do editor (o segundo
    // efeito cuida da troca de estilo).
  }, []);

  /*
   * O CSS do corpo mudou (a pessoa trocou o formato do parágrafo): reescreve a folha
   * de estilo de dentro do `iframe`, sem tocar no texto nem no cursor.
   */
  useEffect(() => {
    const atual = editor.current;
    if (!atual) return;
    const documento = atual.getBody()?.ownerDocument;
    const folha = documento?.getElementById('estilo-do-documento');
    if (folha) folha.textContent = estiloDoCorpo;
  }, [estiloDoCorpo]);

  /*
   * Conteúdo que veio **de fora**: reinjeta. O gatilho é a `versao` (que só muda
   * quando quem manda é a tela) e não o texto em si — comparar texto não serviria:
   * o motor devolve o HTML com pequenas diferenças de forma, e cada tecla acabaria
   * reinjetando o conteúdo e jogando o cursor para o começo.
   *
   * `religando` silencia o `input` que o `setContent` provoca.
   */
  useEffect(() => {
    const atual = editor.current;
    if (!atual) return;

    /*
     * Editor ainda não pronto: o conteúdo fica guardado e entra no `init`. Chamar o
     * `setContent` aqui seria pedir o estouro do TinyMCE — e o estouro, vindo de um
     * efeito, apagava a tela inteira (era o defeito da segunda abertura do documento).
     */
    if (!pronto.current) {
      pendente.current = html;
      return;
    }

    religando.current = true;
    const falha = darConteudo(atual, html);
    if (falha) setTropeco(falha);
    else ultimoDoEditor.current = html;
    religando.current = false;
    // O HTML entra pela referência de propósito: quem manda reinjetar é a `versao`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao]);

  /*
   * Ctrl+S salva o documento, como em qualquer editor.
   *
   * Este é o atalho de **fora** do texto (o painel, a barra, a lista de referências). Com o
   * cursor dentro do editor quem cuida é o `keydown` do próprio TinyMCE, registrado no
   * `init` — o teclado de dentro do `iframe` não chega até aqui.
   */
  useEffect(() => {
    const atalho = (evento: KeyboardEvent) => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 's') {
        evento.preventDefault();
        aoSalvarAtual.current?.();
      }
    };
    document.addEventListener('keydown', atalho);
    return () => document.removeEventListener('keydown', atalho);
  }, []);

  return (
    <div
      className="rounded-lg border border-border bg-card p-1.5 [&_.tox-tinymce]:rounded-md [&_.tox-tinymce]:border-border"
      data-editor-de-texto=""
    >
      {/*
        O editor tropeçou: a pessoa precisa **ler** isso. Antes, a exceção subia e o
        React desmontava a página — o texto sumia junto, sem aviso nenhum.
      */}
      {tropeco ? (
        <p
          className="mb-1.5 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
          data-tropeco-do-editor=""
        >
          O editor de texto não conseguiu abrir o conteúdo ({tropeco}). Recarregue a página; se o aviso
          voltar, o documento continua salvo e íntegro — só a escrita na tela ficou fora.
        </p>
      ) : null}
      <div ref={caixa} />
    </div>
  );
}
